import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Activity, ChevronRight, Clock } from 'lucide-react';
import type { UseQueryResult } from '@tanstack/react-query';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { cn } from '@/lib/cn';
import type { ProviderStatus, ProviderStatusKind, ProvidersStatus } from '@/lib/providers/types';
import { relativeTime } from './format';

/**
 * "Recent traffic" — the observed health per LOGICAL provider (flight inventory,
 * airline, risk prediction, hotels, payments) from rebooking's Redis-backed
 * registry, pushed over the WS (no poll): state, last call, request / throttle /
 * error counts, latency, and the vendor-egress rate-limit picture. It answers a
 * different question from the service cards above (traffic already sent, not
 * configuration), so it is one compact table at the bottom, CLOSED by default.
 *
 * The throttled banner stays outside the fold: a provider that is rate-limiting
 * us right now is the one fact here an operator must see without opening it —
 * slow rebookings are then a third-party degradation, not a system fault.
 */

const STATUS_VARIANT: Record<ProviderStatusKind, BadgeVariant> = {
  HEALTHY: 'success',
  THROTTLED: 'warning',
  DEGRADED: 'error',
  IDLE: 'muted',
};

export function RecentTraffic({ query }: { query: UseQueryResult<ProvidersStatus> }) {
  const { t } = useTranslation('network');
  const [open, setOpen] = useState(false);
  const bodyId = useId();
  const { data, isLoading, isError } = query;

  return (
    <Card className="overflow-hidden" data-testid="providers-recent-traffic" data-open={open}>
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-4 py-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={open ? bodyId : undefined}
          className="flex min-h-11 min-w-0 flex-1 items-center gap-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid="providers-recent-traffic-toggle"
        >
          <ChevronRight
            className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-90')}
            aria-hidden
          />
          <Activity className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          <span className="flex min-w-0 flex-col">
            <span className="font-semibold">{t('providers.traffic.title')}</span>
            <span className="text-xs text-muted-foreground">{t('providers.traffic.subtitle')}</span>
          </span>
        </button>
        {data ? (
          <span className="text-[11px] text-muted-foreground">
            {t('providers.updated', { when: relativeTime(data.computedAt, t) })}
          </span>
        ) : isError ? (
          <span className="text-[11px] text-muted-foreground">{t('providers.traffic.unavailable')}</span>
        ) : null}
      </div>

      {data && data.throttled.length > 0 ? (
        <div
          className="flex items-center gap-2 border-t border-amber-500/40 bg-amber-500/10 px-4 py-2 text-sm text-amber-800 dark:text-amber-300"
          data-testid="providers-throttled-banner"
        >
          <Clock className="h-4 w-4 shrink-0" aria-hidden />
          {t('providers.throttledBanner', { count: data.throttled.length })}
        </div>
      ) : null}

      {open ? (
        <div id={bodyId} className="border-t" data-testid="providers-recent-traffic-body">
          {isLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-8 w-full" />
              ))}
            </div>
          ) : isError || !data ? (
            <EmptyState
              icon={<Clock className="h-8 w-8" aria-hidden />}
              title={t('providers.loadErrorTitle')}
              description={t('providers.loadErrorHint')}
              className="p-6"
            />
          ) : (
            <TrafficTable providers={data.providers} />
          )}
        </div>
      ) : null}
    </Card>
  );
}

function TrafficTable({ providers }: { providers: ProviderStatus[] }) {
  const { t } = useTranslation('network');
  const th = 'px-3 py-2 text-left font-semibold';
  const td = 'px-3 py-2 align-top';
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[860px] border-collapse text-sm" data-testid="providers-grid">
        <thead className="border-b bg-muted/40 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
          <tr>
            <th scope="col" className={th}>
              {t('providers.traffic.service')}
            </th>
            <th scope="col" className={th}>
              {t('providers.table.status')}
            </th>
            <th scope="col" className={th}>
              {t('providers.card.lastCall')}
            </th>
            <th scope="col" className={cn(th, 'text-right')}>
              {t('providers.card.requests')}
            </th>
            <th scope="col" className={cn(th, 'text-right')}>
              {t('providers.card.latencyAvgP95')}
            </th>
            <th scope="col" className={cn(th, 'text-right')}>
              {t('providers.card.rateLimits')}
            </th>
            <th scope="col" className={cn(th, 'text-right')}>
              {t('providers.card.errors')}
            </th>
            <th scope="col" className={th}>
              {t('providers.card.rateLimit')}
            </th>
          </tr>
        </thead>
        <tbody>
          {providers.map((p) => (
            <tr key={p.vendor} className="border-b last:border-b-0" data-testid={`provider-card-${p.vendor}`}>
              <th scope="row" className={cn(td, 'text-left font-medium first-letter:uppercase')}>
                {t(`providers.vendor.${p.vendor}`, { defaultValue: p.vendor })}
              </th>
              <td className={td}>
                <Badge variant={STATUS_VARIANT[p.status]}>{t(`providers.status.${p.status}`)}</Badge>
                {p.status === 'THROTTLED' && p.lastRetryAfterMs ? (
                  <span className="mt-1 block text-xs text-amber-700 dark:text-amber-400">
                    {t('providers.card.retryAfter')} ~{Math.round(p.lastRetryAfterMs / 100) / 10}s
                  </span>
                ) : null}
              </td>
              <td className={cn(td, 'text-xs text-muted-foreground')}>
                {p.lastSeenAt ? relativeTime(p.lastSeenAt, t) : '—'}
              </td>
              <td className={cn(td, 'text-right tabular-nums')}>{p.requestCount ?? '—'}</td>
              <td className={cn(td, 'text-right tabular-nums')}>
                {p.avgLatencyMs != null
                  ? `${p.avgLatencyMs}${p.p95LatencyMs != null ? ` / ${p.p95LatencyMs}` : ''} ms`
                  : p.lastLatencyMs != null
                    ? `${p.lastLatencyMs} ms`
                    : '—'}
              </td>
              <td className={cn(td, 'text-right tabular-nums', p.throttleCount ? 'text-amber-700 dark:text-amber-400' : '')}>
                {p.throttleCount || '—'}
              </td>
              <td className={cn(td, 'text-right tabular-nums', p.errorCount ? 'text-rose-700 dark:text-rose-400' : '')}>
                {p.errorCount || '—'}
              </td>
              <td className={cn(td, 'text-xs')}>
                {p.rateLimit?.configured || p.rateLimit?.saturatedForMs || p.rateLimit?.breakerState ? (
                  <div className="space-y-0.5" data-testid={`provider-ratelimit-${p.vendor}`}>
                    {p.rateLimit?.configured ? (
                      <p className="break-words font-mono text-muted-foreground">{p.rateLimit.configured}</p>
                    ) : null}
                    {p.rateLimit?.saturatedForMs ? (
                      <p className="text-amber-700 dark:text-amber-400">
                        {t('providers.card.saturation')} ~{Math.round(p.rateLimit.saturatedForMs / 100) / 10}s
                      </p>
                    ) : null}
                    {p.rateLimit?.breakerState ? (
                      <p className="font-medium text-rose-700 dark:text-rose-400">
                        {t('providers.card.breaker')} {p.rateLimit.breakerState}
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
